import type { Config, OpencodeClient, Path, Project, ProviderAuthResponse, Todo } from "@openanalyst-ai/sdk/v2/client"
import { showToast } from "@/utils/toast"
import { getFilename } from "@openanalyst-ai/core/util/path"
import { batch, createSignal, getOwner, onCleanup, onMount, untrack } from "solid-js"
import { createStore, produce, reconcile } from "solid-js/store"
import { useLanguage } from "@/context/language"
import type { InitError } from "../pages/error"
import { ServerSDK, useServerSDK } from "./server-sdk"
import {
  bootstrapDirectory,
  bootstrapGlobal,
  clearProviderRev,
  loadAgentsQuery,
  loadGlobalConfigQuery,
  loadPathQuery,
  loadProjectsQuery,
  loadProvidersQuery,
} from "./global-sync/bootstrap"
import { createChildStoreManager } from "./global-sync/child-store"
import { applyDirectoryEvent, applyGlobalEvent, cleanupDroppedSessionCaches } from "./global-sync/event-reducer"
import { clearSessionPrefetchDirectory } from "./global-sync/session-prefetch"
import { estimateRootSessionTotal, loadRootSessionsWithFallback } from "./global-sync/session-load"
import { trimSessions } from "./global-sync/session-trim"
import type { ProjectMeta } from "./global-sync/types"
import { SESSION_RECENT_LIMIT } from "./global-sync/types"
import { queryOptions, skipToken, useMutation, useQueries, useQuery, useQueryClient } from "@tanstack/solid-query"
import { createRefreshQueue } from "./global-sync/queue"
import { authTokenReady, onAuthTokenChange } from "./auth/token"
import { directoryKey } from "./global-sync/utils"
import { PathKey } from "@/utils/path-key"
import { createDirSyncContext } from "./directory-sync"
import { createSimpleContext, NormalizedProviderListResponse } from "@openanalyst-ai/ui/context"
import { createRefCountMap } from "@/utils/refcount"
import { useGlobal } from "./global"
import { ServerConnection, useServer } from "./server"
import { retry } from "@openanalyst-ai/core/util/retry"
import type { ServerScope } from "@/utils/server-scope"
import { persisted } from "@/utils/persist"
import { toggleMcp } from "./global-sync/mcp"
import {
  clearServerSyncProjectRefreshFailedToast,
  isAuthNotReadyError,
  serverSyncProjectRefreshFailedToast,
  serverSyncSessionsLoadFailedMessage,
  sessionLoadFailureDetail,
  sessionLoadRetryDelayMs,
  shouldRetrySessionLoad,
  shouldShowServerSyncProjectRefreshFailedToast,
} from "./server-sync-copy"

type GlobalStore = {
  ready: boolean
  error?: InitError
  path: Path
  project: Project[]
  session_todo: {
    [sessionID: string]: Todo[]
  }
  provider: NormalizedProviderListResponse
  provider_auth: ProviderAuthResponse
  config: Config
  reload: undefined | "pending" | "complete"
}

function isOpencodeClient(input: unknown): input is OpencodeClient {
  return typeof input === "object" && input !== null && "mcp" in input
}

export const loadMcpQuery = (
  scopeOrDirectory: ServerScope | string,
  directoryOrSdk?: string | OpencodeClient,
  sdkMaybe?: OpencodeClient,
) => {
  const scoped = isOpencodeClient(sdkMaybe)
  const sdk = scoped ? sdkMaybe : isOpencodeClient(directoryOrSdk) ? directoryOrSdk : undefined
  const directory = scoped ? (directoryOrSdk as string) : scopeOrDirectory
  return queryOptions({
    queryKey: scoped ? [scopeOrDirectory, directory, "mcp"] : [directory, "mcp"],
    queryFn: sdk ? () => sdk.mcp.status().then((r) => r.data ?? {}) : skipToken,
  })
}

export const loadLspQuery = (
  scopeOrDirectory: ServerScope | string,
  directoryOrSdk?: string | OpencodeClient,
  sdkMaybe?: OpencodeClient,
) => {
  const scoped = isOpencodeClient(sdkMaybe)
  const sdk = scoped ? sdkMaybe : isOpencodeClient(directoryOrSdk) ? directoryOrSdk : undefined
  const directory = scoped ? (directoryOrSdk as string) : scopeOrDirectory
  return queryOptions({
    queryKey: scoped ? [scopeOrDirectory, directory, "lsp"] : [directory, "lsp"],
    queryFn: sdk ? () => sdk.lsp.status().then((r) => r.data ?? []) : skipToken,
  })
}

export const loadSessionsQuery = (scope: ServerScope, directory: string) =>
  queryOptions<null>({ queryKey: [scope, directory, "loadSessions"] as const, queryFn: skipToken })

function makeQueryOptionsApi(
  scope: ServerScope,
  serverSDK: () => OpencodeClient,
  sdkFor: (dir: PathKey) => OpencodeClient,
) {
  return {
    globalConfig: () => loadGlobalConfigQuery(scope, serverSDK()),
    projects: () => loadProjectsQuery(scope, serverSDK()),
    providers: (directory: PathKey | null) =>
      loadProvidersQuery(scope, directory, directory === null ? serverSDK() : sdkFor(directory)),
    path: (directory: PathKey | null) =>
      loadPathQuery(scope, directory, directory === null ? serverSDK() : sdkFor(directory)),
    agents: (directory: PathKey) => loadAgentsQuery(scope, directory, sdkFor(directory)),
    mcp: (directory: PathKey) => loadMcpQuery(scope, directory, sdkFor(directory)),
    lsp: (directory: PathKey) => loadLspQuery(scope, directory, sdkFor(directory)),
    sessions: (directory: PathKey) => loadSessionsQuery(scope, directory),
  }
}
export type QueryOptionsApi = ReturnType<typeof makeQueryOptionsApi>

export function createServerSyncContextInner(_serverSDK?: ServerSDK) {
  const serverSDK: ServerSDK = _serverSDK ?? useServerSDK()
  const language = useLanguage()
  const owner = getOwner()
  if (!owner) throw new Error("ServerSync must be created within owner")

  const sdkCache = new Map<string, OpencodeClient>()
  const booting = new Map<string, Promise<void>>()
  const sessionLoads = new Map<string, Promise<void>>()
  const sessionMeta = new Map<string, { limit: number }>()

  const sdkFor = (directory: string) => {
    const key = directoryKey(directory)
    const cached = sdkCache.get(key)
    if (cached) return cached
    const sdk = serverSDK.createClient({
      directory,
      throwOnError: true,
    })
    sdkCache.set(key, sdk)
    return sdk
  }

  const queryOptionsApi = makeQueryOptionsApi(serverSDK.scope, () => serverSDK.client, sdkFor)

  // Reactive bridge over the module-level token state: TanStack `enabled` needs a
  // signal it can track, and token.ts stays framework-agnostic. Fed by the
  // onAuthTokenChange listener below; starts from the current readiness so a mount
  // with the token already in hand (every authenticated mount) fetches immediately.
  const [tokenReady, setTokenReady] = createSignal(authTokenReady())

  // These observers fetch on mount, token or not — during the signed-out window that
  // was a doomed 401 per query (with retries). `enabled` holds them until a token is
  // ready; the flip auto-fetches. Readiness fails open for tokenless runtimes.
  const [configQuery, providerQuery, pathQuery] = useQueries(() => ({
    queries: [
      { ...queryOptionsApi.globalConfig(), enabled: tokenReady() },
      { ...queryOptionsApi.providers(null), enabled: tokenReady() },
      { ...queryOptionsApi.path(null), enabled: tokenReady() },
    ],
  }))

  const [globalStore, setGlobalStore] = createStore<GlobalStore>({
    get ready() {
      return !bootstrap.isPending
    },
    project: [],
    session_todo: {},
    provider_auth: {},
    get path() {
      const EMPTY = { state: "", config: "", worktree: "", directory: "", home: "" }
      if (pathQuery.isLoading) return EMPTY
      return pathQuery.data ?? EMPTY
    },
    get provider() {
      const EMPTY = { all: [], connected: [], default: {} }
      if (providerQuery.isLoading) return EMPTY
      return providerQuery.data ?? EMPTY
    },
    get config() {
      if (configQuery.isLoading) return {}
      return configQuery.data ?? {}
    },
    get reload() {
      return updateConfigMutation.isPending ? "pending" : undefined
    },
  })

  const queryClient = useQueryClient()

  let bootedAt = 0
  let bootingRoot = false
  let eventFrame: number | undefined
  let eventTimer: ReturnType<typeof setTimeout> | undefined

  // A stale/unreachable project can fire MCP events ~once per second; without a cooldown each one
  // re-runs command.list() and shows the refresh-failed toast every second. After a failure we back
  // off before retrying that directory (and don't re-toast within the window); a success clears it.
  const mcpRetryAfter = new Map<string, number>()
  const MCP_RETRY_COOLDOWN_MS = 30_000

  onCleanup(() => {
    if (eventFrame !== undefined) cancelAnimationFrame(eventFrame)
    if (eventTimer !== undefined) clearTimeout(eventTimer)
  })

  const setProjects = (next: Project[] | ((draft: Project[]) => Project[])) => {
    setGlobalStore("project", next)
  }

  const setBootStore = ((...input: unknown[]) => {
    if (input[0] === "project" && Array.isArray(input[1])) {
      setProjects(input[1] as Project[])
      return input[1]
    }
    return (setGlobalStore as (...args: unknown[]) => unknown)(...input)
  }) as typeof setGlobalStore

  const bootstrap = useQuery(() => ({
    queryKey: [serverSDK.scope, "bootstrap"],
    // Its queryFn fans out to every global fetch, so this is the load-bearing gate.
    enabled: tokenReady(),
    queryFn: async () => {
      await bootstrapGlobal({
        serverSDK: serverSDK.client,
        scope: serverSDK.scope,
        requestFailedTitle: language.t("common.requestFailed"),
        translate: language.t,
        formatMoreCount: (count) => language.t("common.moreCountSuffix", { count }),
        setGlobalStore: setBootStore,
        queryClient,
      })
      bootedAt = Date.now()
      return bootedAt
    },
  }))

  const set = ((...input: unknown[]) => {
    if (input[0] === "project" && (Array.isArray(input[1]) || typeof input[1] === "function")) {
      setProjects(input[1] as Project[] | ((draft: Project[]) => Project[]))
      return input[1]
    }
    return (setGlobalStore as (...args: unknown[]) => unknown)(...input)
  }) as typeof setGlobalStore

  const setSessionTodo = (sessionID: string, todos: Todo[] | undefined) => {
    if (!sessionID) return
    if (!todos) {
      setGlobalStore(
        "session_todo",
        produce((draft) => {
          delete draft[sessionID]
        }),
      )
      return
    }
    setGlobalStore("session_todo", sessionID, reconcile(todos, { key: "id" }))
  }

  // Signing out clears the in-memory cloud token but leaves the app mounted, so anything that
  // refreshes while the user is on the login screen fires without one and comes back 401. Pausing
  // keeps those directories queued instead of burning them on a request that cannot succeed.
  const paused = () => untrack(() => globalStore.reload) !== undefined || !authTokenReady()

  const queue = createRefreshQueue({
    paused,
    key: directoryKey,
    bootstrap: () => queryClient.fetchQuery({ queryKey: [serverSDK.scope, "bootstrap"] }),
    bootstrapInstance,
  })

  // The pause above is only half of it: a token can arrive a minute later, once the user has
  // finished typing an email and an OTP, and nothing else in this layer is watching for it. Drain
  // the queue and refetch what failed the moment one lands, so the lists fill themselves in
  // instead of leaving the user staring at load errors from before they signed in.
  onMount(() => {
    onCleanup(
      onAuthTokenChange(() => {
        setTokenReady(authTokenReady())
        if (!authTokenReady()) return
        queue.refresh()
        void queryClient.invalidateQueries({ queryKey: [serverSDK.scope] })
      }),
    )
  })

  const children = createChildStoreManager({
    owner,
    scope: serverSDK.scope,
    persist: persisted,
    isBooting: (directory) => booting.has(directory),
    isLoadingSessions: (directory) => sessionLoads.has(directory),
    onBootstrap: (directory) => {
      void bootstrapInstance(directory)
    },
    onMcp: (directory, setStore) => {
      const key = directoryKey(directory)
      // Back off a directory that just failed: a broken project must not spin command.list + toast
      // every second (the toast label maps to the failing project, not the active one).
      if (Date.now() < (mcpRetryAfter.get(key) ?? 0)) return
      void retry(() =>
        sdkFor(directory)
          .command.list()
          .then((x) => setStore("command", x.data ?? [])),
      )
        .then(() => mcpRetryAfter.delete(key))
        .catch((err) => {
          mcpRetryAfter.set(key, Date.now() + MCP_RETRY_COOLDOWN_MS)
          console.error("[server-sync] command list failed", err)
          // Same as the bootstrap batch: a missing token is not a broken project, and the
          // token-change listener re-runs this once one lands.
          if (isAuthNotReadyError(err)) {
            mcpRetryAfter.delete(key)
            return
          }
          if (!shouldShowServerSyncProjectRefreshFailedToast(directory)) return
          showToast(serverSyncProjectRefreshFailedToast(getFilename(directory), language.t))
        })
    },
    onDispose: (directory) => {
      const key = directoryKey(directory)
      queue.clear(key)
      sessionMeta.delete(key)
      sdkCache.delete(key)
      clearProviderRev(serverSDK.scope, key)
      mcpRetryAfter.delete(key)
      clearServerSyncProjectRefreshFailedToast(key)
      clearSessionPrefetchDirectory(serverSDK.scope, key)
    },
    translate: language.t,
    queryOptions: queryOptionsApi,
    global: {
      provider: globalStore.provider,
    },
  })

  async function loadSessions(directory: string, options?: { limit?: number }) {
    // The sidebar and layout call this directly when a project row mounts, which is
    // the one load path left that skipped the token gate — during the sign-in window
    // every such call 401'd, burned its retry budget, and toasted. Same treatment as
    // bootstrapInstance: park the directory on the queue and let the token-arrival
    // listener run it, sessions included, once a token is actually in hand.
    if (!authTokenReady()) {
      queue.push(directory)
      return
    }
    const key = directoryKey(directory)
    const pending = sessionLoads.get(key)
    if (pending) {
      await pending
      return loadSessions(directory, options)
    }

    children.pin(key)
    const [store, setStore] = children.child(directory, { bootstrap: false })
    const meta = sessionMeta.get(key)
    const retainedLimit = Math.max(store.limit, options?.limit ?? 0, meta?.limit ?? 0)
    if (meta && meta.limit >= retainedLimit) {
      const next = trimSessions(store.session, {
        limit: retainedLimit,
        permission: store.permission,
      })
      if (next.length !== store.session.length) {
        setStore("session", reconcile(next, { key: "id" }))
        cleanupDroppedSessionCaches(store, setStore, next, setSessionTodo)
      }
      children.unpin(key)
      return
    }

    const limit = Math.max(retainedLimit + SESSION_RECENT_LIMIT, SESSION_RECENT_LIMIT)
    const promise = queryClient
      .fetchQuery({
        ...queryOptionsApi.sessions(key),
        queryFn: async () => {
          const applySessions = (x: Awaited<ReturnType<typeof loadRootSessionsWithFallback>>) => {
            const nonArchived = (x.data ?? [])
              .filter((s) => !!s?.id)
              .filter((s) => !s.time?.archived)
              .sort((a, b) => (a.id < b.id ? -1 : a.id > b.id ? 1 : 0))
            const limit = Math.max(store.limit, options?.limit ?? 0, sessionMeta.get(key)?.limit ?? 0)
            const childSessions = store.session.filter((s) => !!s.parentID)
            const sessions = trimSessions([...nonArchived, ...childSessions], {
              limit,
              permission: store.permission,
            })
            batch(() => {
              setStore(
                "sessionTotal",
                estimateRootSessionTotal({
                  count: nonArchived.length,
                  limit: x.limit,
                  limited: x.limited,
                }),
              )
              setStore("session", reconcile(sessions, { key: "id" }))
              cleanupDroppedSessionCaches(store, setStore, sessions, setSessionTodo)
            })
            sessionMeta.set(key, { limit })
          }

          // Retry while the failure is only "the auth token has not landed yet" — the
          // sign-in race described in server-sync-copy.ts. Staying inside this queryFn
          // keeps the pending promise in `sessionLoads`, so retries never fan out into
          // duplicate loads for the same directory.
          for (let attempt = 1; ; attempt++) {
            try {
              applySessions(
                await loadRootSessionsWithFallback({
                  directory,
                  limit,
                  list: (query) => serverSDK.client.session.list(query),
                }),
              )
              return null
            } catch (err) {
              const delay = shouldRetrySessionLoad(err, attempt) ? sessionLoadRetryDelayMs(attempt) : undefined
              if (delay !== undefined) {
                await new Promise((resolve) => setTimeout(resolve, delay))
                continue
              }
              console.error("[server-sync] session list failed", err)
              // A load that was already in flight when sign-out cleared the token dies
              // with a 401 the user cannot act on; the token listener re-runs it. Only
              // failures that would survive a valid token deserve a toast.
              if (!isAuthNotReadyError(err)) {
                const project = getFilename(directory)
                showToast({
                  variant: "error",
                  title: language.t("toast.session.listFailed.title", { project }),
                  description: serverSyncSessionsLoadFailedMessage(sessionLoadFailureDetail(err)),
                })
              }
              return null
            }
          }
        },
      })
      .then(() => {})

    sessionLoads.set(key, promise)
    void promise.finally(() => {
      sessionLoads.delete(key)
      children.unpin(key)
    })
    return promise
  }

  async function bootstrapInstance(directory: string) {
    const key = directoryKey(directory)
    if (!key) return
    // The refresh queue's `paused` gate does not cover this: child stores bootstrap themselves on
    // first access and call straight through. Without a token the whole batch can only 401, so hand
    // the directory to the queue instead and let the token listener drain it once one lands.
    if (!authTokenReady()) {
      queue.push(directory)
      return
    }
    const pending = booting.get(key)
    if (pending) return pending

    children.pin(key)
    const promise = Promise.resolve().then(async () => {
      const child = children.ensureChild(directory)
      const cache = children.vcsCache.get(key)
      if (!cache) return
      const sdk = sdkFor(directory)
      await bootstrapDirectory({
        directory,
        scope: serverSDK.scope,
        mcp: children.mcp(key),
        global: {
          config: globalStore.config,
          path: globalStore.path,
          project: globalStore.project,
          provider: globalStore.provider,
        },
        sdk,
        store: child[0],
        setStore: child[1],
        vcsCache: cache,
        loadSessions,
        translate: language.t,
        queryClient,
      })
    })

    booting.set(key, promise)
    void promise.finally(() => {
      booting.delete(key)
      children.unpin(key)
    })
    return promise
  }

  const unsub = serverSDK.event.listen((e) => {
    const directory = e.name
    const key = directoryKey(directory)
    const event = e.details
    const recent = bootingRoot || Date.now() - bootedAt < 1500

    if (directory === "global") {
      applyGlobalEvent({
        event,
        project: globalStore.project,
        refresh: () => {
          if (recent) return
          // refetch() ignores `enabled`, and these events ride the auth-exempt
          // /event stream, so they can arrive while signed out.
          if (!authTokenReady()) return
          bootstrap.refetch()
        },
        setGlobalProject: setProjects,
      })
      if (event.type === "server.connected" || event.type === "global.disposed") {
        if (recent) return
        for (const directory of Object.keys(children.children)) {
          queue.push(directory)
        }
      }
      return
    }

    const existing = children.children[key]
    if (!existing) return
    children.mark(key)
    const [store, setStore] = existing
    applyDirectoryEvent({
      event,
      directory,
      store,
      setStore,
      push: queue.push,
      setSessionTodo,
      retainedLimit: sessionMeta.get(key)?.limit,
      vcsCache: children.vcsCache.get(key),
      loadLsp: () => {
        void queryClient.fetchQuery(queryOptionsApi.lsp(key))
      },
    })
  })

  onCleanup(unsub)
  onCleanup(() => {
    queue.dispose()
  })
  onCleanup(() => {
    for (const directory of Object.keys(children.children)) {
      children.disposeDirectory(directoryKey(directory))
    }
  })

  onMount(() => {
    if (typeof requestAnimationFrame === "function") {
      eventFrame = requestAnimationFrame(() => {
        eventFrame = undefined
        eventTimer = setTimeout(() => {
          eventTimer = undefined
          void serverSDK.event.start()
        }, 0)
      })
    } else {
      eventTimer = setTimeout(() => {
        eventTimer = undefined
        void serverSDK.event.start()
      }, 0)
    }
  })

  const projectApi = {
    loadSessions,
    meta(directory: string, patch: ProjectMeta) {
      children.projectMeta(directory, patch)
    },
    icon(directory: string, value: string | undefined) {
      children.projectIcon(directory, value)
    },
  }

  const updateConfigMutation = useMutation(() => ({
    mutationFn: (config: Config) => serverSDK.client.global.config.update({ config }),
    onSuccess: () => {
      bootstrap.refetch()
      // Invalidate all provider queries so newly configured custom providers
      // appear immediately in the available provider list across all directories.
      queryClient.invalidateQueries({ queryKey: [serverSDK.scope, null, "providers"] })
      queryClient.invalidateQueries({
        predicate: (query) => query.queryKey[0] === serverSDK.scope && query.queryKey[2] === "providers",
      })
    },
  }))

  return {
    scope: serverSDK.scope,
    data: globalStore,
    set,
    get ready() {
      return globalStore.ready
    },
    get error() {
      return globalStore.error
    },
    child: children.child,
    peek: children.peek,
    disableMcp: children.disableMcp,
    queryOptions: queryOptionsApi,
    bootstrap: () => bootstrap.refetch().then(() => undefined),
    updateConfig: updateConfigMutation.mutateAsync,
    project: projectApi,
    todo: {
      set: setSessionTodo,
    },
    mcp: {
      toggle: async (directory: string, name: string) => {
        const key = directoryKey(directory)
        const sdk = sdkFor(key)
        const status = children.child(key, { bootstrap: false })[0].mcp[name].status
        await toggleMcp({
          status,
          connect: async () => {
            await sdk.mcp.connect({ name })
          },
          disconnect: async () => {
            await sdk.mcp.disconnect({ name })
          },
          authenticate: async () => {
            await sdk.mcp.auth.authenticate({ name })
          },
          refresh: async () => {
            await queryClient.refetchQueries(queryOptionsApi.mcp(key))
          },
        })
      },
    },
  }
}

export function createServerSyncContext(_serverSDK?: ServerSDK) {
  const inner = createServerSyncContextInner(_serverSDK)
  return Object.assign(inner, {
    createDirSyncContext: createRefCountMap(
      (dir) => createDirSyncContext(dir, inner, _serverSDK),
      (dir) => inner.disableMcp(dir),
      directoryKey,
    ),
  })
}

export const { use: useServerSync, provider: ServerSyncProvider } = createSimpleContext({
  name: "ServerSync",
  gate: false,
  init: (props: { server?: ServerConnection.Any }) => {
    const global = useGlobal()
    const language = useLanguage()
    const server = useServer()

    const conn = props.server ?? server.current
    if (!conn) throw new Error(language.t("error.serverSDK.noServerAvailable"))
    const ctx = global.createServerCtx(conn)

    return ctx.sync
  },
})

export function useQueryOptions() {
  return useServerSync().queryOptions
}
